import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setActiveFirm as setActiveFirmAction,
    loadActiveFirm,
    loadUserFirms,
    clearActiveFirm
} from '../actions/activeFirm';

/**
 * Custom hook for accessing and managing the global active firm
 * 
 * Usage:
 * const { activeFirm, firms, loading, setActiveFirm, firmId } = useActiveFirm();
 * 
 * @returns {Object} Active firm state and methods
 */
const useActiveFirm = () => {
    const dispatch = useDispatch();
    
    const { 
        activeFirm, 
        userFirms, 
        userFirmsLoading,
        userFirmsError 
    } = useSelector(state => state.firm);
    
    const { user, isAuthenticated } = useSelector(state => state.auth);

    // Load active firm from localStorage on mount
    useEffect(() => {
        dispatch(loadActiveFirm());
    }, [dispatch]);

    // Load user's available firms when authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            dispatch(loadUserFirms());
        }
    }, [dispatch, isAuthenticated, user]);

    // Auto-select first firm if none selected and firms are available
    // This should happen immediately when firms are loaded
    useEffect(() => {
        if (!activeFirm && userFirms.length > 0 && !userFirmsLoading) {
            // Use setTimeout to ensure this runs after the reducer has processed the firms
            const timer = setTimeout(() => {
                dispatch(setActiveFirmAction(userFirms[0]));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [dispatch, activeFirm, userFirms, userFirmsLoading]);

    // Set active firm
    const setActiveFirm = useCallback((firm) => {
        dispatch(setActiveFirmAction(firm));
    }, [dispatch]);

    // Clear active firm
    const clearFirm = useCallback(() => {
        dispatch(clearActiveFirm());
    }, [dispatch]);

    // Refresh firms list
    const refreshFirms = useCallback(() => {
        dispatch(loadUserFirms());
    }, [dispatch]);

    // Get firm by ID from available firms
    const getFirmById = useCallback((id) => {
        return userFirms.find(f => f.id === parseInt(id));
    }, [userFirms]);

    return {
        // Current active firm object
        activeFirm,
        // Shorthand for active firm ID (commonly used in API calls)
        firmId: activeFirm?.id || null,
        // Active firm name
        firmName: activeFirm?.name || null,
        // All available firms for the user
        firms: userFirms,
        // Loading state
        loading: userFirmsLoading,
        // Error state
        error: userFirmsError,
        // Number of available firms
        firmsCount: userFirms.length,
        // Methods
        setActiveFirm,
        clearFirm,
        refreshFirms,
        getFirmById,
        // Helper to check if a specific firm is active
        isActiveFirm: (firmId) => activeFirm?.id === firmId
    };
};

export default useActiveFirm;
